import { useState, useMemo } from 'react';
import Modal from './Modal';
import { useCalendarDataContext } from '../contexts/CalendarDataContext';
import { useTranslation } from '../i18n';
import { HOLIDAYS_PROJECT_ID } from '../utils/constants';
import './ICalExportModal.scss';

/**
 * iCalendar export modal with project selection.
 * Allows users to choose which projects to include in the export.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onExport - Export handler with selectedProjectIds array
 */
function ICalExportModal({ isOpen, onClose, onExport }) {
  // Use data.projects to get ALL projects including the system Holidays project
  // The regular 'projects' array filters out system projects
  const { data } = useCalendarDataContext();
  const { t } = useTranslation();
  
  // Sort projects: Holidays first, then rest in original order
  const sortedProjects = useMemo(() => {
    // Get all projects from raw data
    const allProjects = data?.projects || [];
    
    // Find holidays project (can be identified by isSystem flag or ID)
    const holidayIndex = allProjects.findIndex(p => p.isSystem || p.id === HOLIDAYS_PROJECT_ID);
    
    if (holidayIndex === -1 || holidayIndex === 0) {
      // No holidays project found or already first
      return allProjects;
    }
    
    // Move holidays to first position
    const sorted = [...allProjects];
    const [holidayProject] = sorted.splice(holidayIndex, 1);
    sorted.unshift(holidayProject);
    return sorted;
  }, [data?.projects]);
  
  // Initialize with visible projects checked
  const initialSelection = useMemo(() => {
    const selected = new Set();
    sortedProjects.forEach(project => {
      if (project.visible) {
        selected.add(project.id);
      }
    });
    return selected;
  }, [sortedProjects]);
  
  const [selectedProjects, setSelectedProjects] = useState(() => initialSelection);
  
  const handleToggle = (projectId) => {
    setSelectedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };
  
  const handleSelectAll = () => {
    const allIds = new Set(sortedProjects.map(p => p.id));
    setSelectedProjects(allIds);
  };
  
  const handleDeselectAll = () => {
    setSelectedProjects(new Set());
  };
  
  const handleExport = () => {
    const selectedIds = Array.from(selectedProjects);
    onExport(selectedIds);
  };
  
  const hasSelection = selectedProjects.size > 0;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('icalExport.title')}
      maxWidth={440}
    >
      <div className="ical-export-modal">
        <div className="ical-export-modal__description">
          {t('icalExport.selectProjects')}
        </div>
        
        <div className="ical-export-modal__projects">
          {sortedProjects.map(project => {
            const isChecked = selectedProjects.has(project.id);
            const isHolidays = project.isSystem || project.id === HOLIDAYS_PROJECT_ID;
            const displayName = isHolidays ? t('project.holidaysName') : project.name;
            const hiddenLabel = !project.visible ? ` ${t('icalExport.hiddenSuffix')}` : '';
            
            return (
              <label
                key={project.id}
                className={`ical-export-modal__project ${!project.visible ? 'ical-export-modal__project--hidden' : ''}`}
              >
                <input
                  type="checkbox"
                  className="ical-export-modal__checkbox"
                  checked={isChecked}
                  onChange={() => handleToggle(project.id)}
                />
                <span
                  className={`ical-export-modal__project-dot ${isHolidays ? 'ical-export-modal__project-dot--hidden' : ''}`}
                  style={{ backgroundColor: isHolidays ? 'transparent' : project.color }}
                />
                <span className="ical-export-modal__project-name">
                  {displayName}{hiddenLabel}
                </span>
              </label>
            );
          })}
        </div>
        
        <div className="ical-export-modal__actions">
          <button
            className="ical-export-modal__select-btn"
            onClick={handleSelectAll}
          >
            {t('icalExport.selectAll')}
          </button>
          <button
            className="ical-export-modal__select-btn"
            onClick={handleDeselectAll}
          >
            {t('icalExport.deselectAll')}
          </button>
        </div>
        
        <div className="ical-export-modal__footer">
          <button
            className="ical-export-modal__btn ical-export-modal__btn--cancel"
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
          <button
            className="ical-export-modal__btn ical-export-modal__btn--export"
            onClick={handleExport}
            disabled={!hasSelection}
          >
            {t('icalExport.export')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ICalExportModal;
